import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../AppIcon';

const CHART_COLORS = ['#0D3B7A', '#1B5E20', '#0277BD', '#D84315', '#F57C00', '#6A1B9A'];

const TONE_CLASSES = {
  success: 'text-success',
  warning: 'text-warning',
  accent: 'text-accent',
  error: 'text-destructive',
  neutral: 'text-foreground',
};

const QuickStat = ({ label, value, icon, hint, tone = 'neutral' }) => (
  <div className="bg-background/80 border border-border rounded-lg p-3 min-w-0 hover:border-primary/30 transition-colors">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <Icon name={icon} size={16} className="text-primary" />
      </div>
      <p className="text-xs font-medium text-muted-foreground truncate">{label}</p>
    </div>
    <p className={`text-xl font-bold tabular-nums ${TONE_CLASSES[tone] || TONE_CLASSES.neutral}`}>
      {value}
    </p>
    {hint && <p className="text-[11px] text-muted-foreground mt-1 leading-snug line-clamp-2">{hint}</p>}
  </div>
);

const BreakdownBars = ({ title, items = [] }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (!total) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
        No data for {title?.toLowerCase() || 'this section'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {title && <h4 className="text-sm font-semibold text-foreground">{title}</h4>}
      {items.map((item, index) => {
        const pct = Math.round((item.value / total) * 100);
        return (
          <div key={item.label} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-muted-foreground truncate">{item.label}</span>
              <span className="font-medium text-foreground shrink-0">
                {item.value} ({pct}%)
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${pct}%`,
                  backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BreakdownDonut = ({ title, items = [] }) => {
  const filtered = items.filter((item) => item.value > 0);
  const total = filtered.reduce((sum, item) => sum + item.value, 0);

  if (!total) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
        No data for {title?.toLowerCase() || 'this section'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {title && <h4 className="text-sm font-semibold text-foreground">{title}</h4>}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="w-full sm:w-36 h-36 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={filtered}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={58}
                paddingAngle={2}
              >
                {filtered.map((entry, index) => (
                  <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 w-full space-y-2">
          {filtered.map((item, index) => (
            <div key={item.label} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                />
                <span className="text-muted-foreground truncate">{item.label}</span>
              </div>
              <span className="font-medium text-foreground shrink-0">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const QuickAnalyticsPanel = ({
  title = 'Quick Analytics',
  description,
  stats = [],
  breakdownSections = [],
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden card-shadow animate-pulse">
        <div className="h-1 bg-sidebar" />
        <div className="p-5">
          <div className="h-5 w-40 bg-muted rounded mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden card-shadow">
      <div className="h-1 bg-sidebar" />
      <div className="p-5 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Icon name="BarChart3" size={18} className="text-primary" />
            {title}
          </h2>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {stats.map((stat) => (
            <QuickStat key={stat.label} {...stat} />
          ))}
        </div>

        {breakdownSections.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4 border-t border-border">
            {breakdownSections.map((section) =>
              section.chartType === 'donut' ? (
                <BreakdownDonut key={section.title} title={section.title} items={section.items} />
              ) : (
                <BreakdownBars key={section.title} title={section.title} items={section.items} />
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default QuickAnalyticsPanel;
